import findKey from 'lodash/findKey'
import isString from 'lodash/isString'
import mapKeys from 'lodash/mapKeys'

import {isEmailAddress, isTenDigitPhoneNumber} from '../../utils'

// FORM FIELD MAPPING ////////////////////////////////////////////////

/*
  These functions handle converting redux form field names to api endpoint field names
  and visa versa.
  The api_field is the property sent to the api endpoint.
  The formField is the redux form "name" of the field.
*/

// { api_field: formField }
export const vendorFormKeyMapping = {
  company_name: 'companyName',
  email: 'email',
  contact_first_name: 'firstName',
  contact_last_name: 'lastName',
  phone: 'phone',
  website: 'website',
}

export const addressFormKeyMapping = {
  address_1: 'address',
  city: 'city',
  state: 'state',
  zip_code: 'zipCode',
}

const apiFieldName = (mapping) => (formField) => findKey(mapping, (value) => value === formField)
const formFieldName = (mapping) => (payloadField) => mapping[payloadField]
const renameKeys = (newName) => (formValues) =>
  mapKeys(formValues, (value, key) => newName(key) || key)

const getVendorApiField = apiFieldName(vendorFormKeyMapping)
const getVendorFormField = formFieldName(vendorFormKeyMapping)
export const renameVendorFormFieldsToApiFields = renameKeys(getVendorApiField)
export const renameVendorApiFieldsToFormFields = renameKeys(getVendorFormField)

const getAddressApiField = apiFieldName(addressFormKeyMapping)
const getAddressFormField = formFieldName(addressFormKeyMapping)
export const renameAddressFormFieldsToApiFields = renameKeys(getAddressApiField)
export const renameAddressApiFieldsToFormFields = renameKeys(getAddressFormField)

// ERRORS //////////////////////////////////////////////////////////////
const REQUIRED_ERROR = 'Required'
const EMAIL_ERROR = 'Invalid email address'
const PHONE_ERROR = 'Invalid phone number'
const ZIP_CODE_ERROR = 'Invalid zip code'

// REDUX FORM FIELD VALIDATION //////////////////////////////////////////
export const trimmed = (value) => (isString(value) ? value.trim() : value)
export const isFilled = (value) => !!trimmed(value)
export const required = (value) => (isFilled(value) ? undefined : REQUIRED_ERROR)

// These functions are simple length-based validators.
// More rigorous validation is performed on the backend
export const emailAddress = (value) => (isEmailAddress(value) ? undefined : EMAIL_ERROR)
export const phoneNumberOrNone = (value) => !value || isTenDigitPhoneNumber(value)
export const optionalPhoneNumber = (value) =>
  phoneNumberOrNone(trimmed(value)) ? undefined : PHONE_ERROR
export const validZipCodeLength = (value) => value && value.length >= 5 && value.length <= 10
export const zipCode = (value) => (validZipCodeLength(trimmed(value)) ? undefined : ZIP_CODE_ERROR)

// US STATES /////////////////////////////////////////////////////////

const US_STATES = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['DC', 'District of Columbia'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
]

const stateAbbreviation = (state) => state[0]
const stateName = (state) => state[1]

export const stateChoices = US_STATES.map((state) => ({
  value: stateAbbreviation(state),
  label: stateName(state),
}))
